import { useState } from "react";
import { api, fmtErr } from "../lib/api";
import { toast } from "sonner";
import { Fire, ArrowsClockwise, X, SealCheck } from "@phosphor-icons/react";
import { Modal, Field, ModalFooter } from "../pages/Contacts";

const PROVIDER_LABELS = {
  microsoft: "Microsoft 365", google: "Google Workspace", proofpoint: "Proofpoint",
  mimecast: "Mimecast", barracuda: "Barracuda", other: "Other", unknown: "Unknown",
};

export function WarmupWaveDialog({ onClose, onSaved }) {
  const [size, setSize] = useState(150);
  const [maxPerDomain, setMaxPerDomain] = useState(5);
  const [includeRisky, setIncludeRisky] = useState(false);
  const [preview, setPreview] = useState(null);
  const [busy, setBusy] = useState(false);
  const [creating, setCreating] = useState(false);

  const generate = async () => {
    setBusy(true);
    try {
      const r = await api.warmupPreview({ size: Number(size) || 150, max_per_domain: Number(maxPerDomain) || 5, include_risky: includeRisky });
      setPreview(r);
      if (r.selection.length === 0) toast.info("No eligible contacts left — everyone is already in a wave or flagged undeliverable/risky");
    } catch (e) {
      toast.error(fmtErr(e));
    } finally { setBusy(false); }
  };

  const removeOne = (id) => setPreview(p => ({ ...p, selection: p.selection.filter(s => s.id !== id) }));

  const create = async () => {
    setCreating(true);
    try {
      const r = await api.warmupCreate({ contact_ids: preview.selection.map(s => s.id) });
      toast.success(`"${r.name}" created with ${r.member_count} contacts — pick it as your campaign audience`);
      onSaved();
    } catch (e) {
      toast.error(fmtErr(e));
      setCreating(false);
    }
  };

  return (
    <Modal title="Build warm-up wave" subtitle="Auto-selects a diverse, gateway-safe sample: capped per company domain, balanced across mail providers. Excludes anyone already emailed, reserved in an existing wave, or undeliverable/risky." onClose={onClose} wide>
      <div className="flex items-end gap-3 flex-wrap">
        <Field label="Wave size">
          <input type="number" min={10} max={25000} data-testid="warmup-size" value={size} onChange={e => setSize(e.target.value)} className="modal-input" style={{ width: 110 }} />
        </Field>
        <Field label="Max per company domain">
          <input type="number" min={1} max={500} data-testid="warmup-max-domain" value={maxPerDomain} onChange={e => setMaxPerDomain(e.target.value)} className="modal-input" style={{ width: 110 }} />
        </Field>
        <label className="flex items-center gap-1.5 text-xs text-zinc-700 cursor-pointer pb-2.5">
          <input type="checkbox" data-testid="warmup-include-risky" checked={includeRisky} onChange={e => setIncludeRisky(e.target.checked)} />
          Include risky addresses <span className="text-zinc-400">(best left off while warming up)</span>
        </label>
        <button onClick={generate} disabled={busy} data-testid="warmup-generate"
          className="inline-flex items-center gap-1.5 text-sm px-3 py-2 rounded-md bg-zinc-950 text-white hover:bg-zinc-800 disabled:opacity-50">
          <ArrowsClockwise size={14} className={busy ? "animate-spin" : ""} /> {busy ? "Building… (checking mail providers)" : preview ? "Regenerate" : "Generate preview"}
        </button>
      </div>

      {preview && (
        <div className="mt-4 space-y-3">
          <div className="flex flex-wrap gap-1.5 items-center" data-testid="warmup-breakdown">
            <span className="text-[11px] font-semibold px-2 py-1 rounded-full bg-zinc-950 text-white">Wave {preview.wave_number}</span>
            <span className="text-[11px] font-medium px-2 py-1 rounded-full border border-zinc-200 bg-zinc-50 text-zinc-700">
              {preview.selection.length} contacts · {new Set(preview.selection.map(s => s.domain)).size} domains
            </span>
            {Object.entries(preview.counts.providers).map(([p, n]) => (
              <span key={p} className="text-[11px] px-2 py-1 rounded-full border border-sky-200 bg-sky-50 text-sky-700">{PROVIDER_LABELS[p] || p}: {n}</span>
            ))}
            <span className="text-[11px] px-2 py-1 rounded-full border border-emerald-200 bg-emerald-50 text-emerald-700">{preview.counts.verified} verified</span>
            {preview.counts.risky > 0 && <span className="text-[11px] px-2 py-1 rounded-full border border-amber-200 bg-amber-50 text-amber-700">{preview.counts.risky} risky</span>}
            <span className="text-[11px] px-2 py-1 rounded-full border border-zinc-200 bg-zinc-50 text-zinc-600">{preview.counts.unverified} unverified</span>
          </div>

          {preview.breakdown && (
            <p className="text-[11px] text-zinc-500" data-testid="warmup-pool-summary">
              Eligible pool: <span className="font-semibold text-zinc-800">{preview.eligible_total.toLocaleString()}</span> never-emailed contacts
              &nbsp;·&nbsp;excluded: {preview.breakdown.already_emailed.toLocaleString()} already emailed,
              &nbsp;{preview.breakdown.reserved_in_waves.toLocaleString()} reserved in existing waves,
              &nbsp;{preview.breakdown.excluded_bucket.toLocaleString()} undeliverable{includeRisky ? "" : "/risky"}
            </p>
          )}

          {preview.selection.length < preview.requested_size && (
            <p className="text-xs text-sky-800 bg-sky-50 border border-sky-200 rounded-md px-2.5 py-1.5" data-testid="warmup-shortfall-note">
              {preview.eligible_total <= preview.selection.length
                ? <>You asked for {preview.requested_size.toLocaleString()} but only {preview.selection.length.toLocaleString()} could be selected — that's every eligible contact left. Prospect more contacts or delete an unused wave list to free up its reserved members.</>
                : <>You asked for {preview.requested_size.toLocaleString()} but got {preview.selection.length.toLocaleString()} — {preview.domains_at_cap} compan{preview.domains_at_cap === 1 ? "y" : "ies"} hit the {Number(maxPerDomain)}-per-domain cap. Raise "Max per company domain" to pull more from big companies.</>}
            </p>
          )}

          {preview.counts.unverified > 0 && (
            <p className="text-xs text-amber-700 bg-amber-50 border border-amber-200 rounded-md px-2.5 py-1.5 flex items-center gap-1.5" data-testid="warmup-verify-reminder">
              <SealCheck size={13} className="shrink-0" /> {preview.counts.unverified} contacts are unverified — run the Deliverability check in your campaign before sending (0.5 Hunter credits per address).
            </p>
          )}

          <div className="border border-zinc-200 rounded-md max-h-72 overflow-auto" data-testid="warmup-selection">
            <table className="w-full text-xs">
              <thead className="sticky top-0 bg-zinc-50 text-zinc-500">
                <tr className="text-left">
                  <th className="px-2.5 py-1.5 font-medium">Name</th>
                  <th className="px-2.5 py-1.5 font-medium">Company</th>
                  <th className="px-2.5 py-1.5 font-medium">Domain</th>
                  <th className="px-2.5 py-1.5 font-medium">Provider</th>
                  <th className="px-2.5 py-1.5"></th>
                </tr>
              </thead>
              <tbody>
                {preview.selection.map(s => (
                  <tr key={s.id} className="border-t border-zinc-100">
                    <td className="px-2.5 py-1.5 text-zinc-900">{s.first_name} {s.last_name}</td>
                    <td className="px-2.5 py-1.5 text-zinc-600 truncate max-w-[160px]">{s.company}</td>
                    <td className="px-2.5 py-1.5 font-mono text-zinc-500">{s.domain}</td>
                    <td className="px-2.5 py-1.5 text-zinc-600">{PROVIDER_LABELS[s.provider] || s.provider}</td>
                    <td className="px-2.5 py-1.5 text-right">
                      <button onClick={() => removeOne(s.id)} data-testid={`warmup-remove-${s.id}`} className="text-zinc-300 hover:text-rose-600"><X size={13} /></button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}

      <ModalFooter onClose={onClose}>
        <button data-testid="warmup-create" disabled={!preview || preview.selection.length === 0 || creating} onClick={create} className="modal-btn-primary inline-flex items-center gap-1.5">
          <Fire size={14} /> {creating ? "Creating…" : `Create list (${preview?.selection.length || 0} contacts)`}
        </button>
      </ModalFooter>
    </Modal>
  );
}
